import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';

/**
 * Custom hook for server-side search in Autocomplete components.
 * Handles debounced search, loading states, and paginated responses.
 *
 * @param {string} endpoint - API endpoint (e.g., '/api/parts/')
 * @param {Object} options - Configuration options
 * @param {number} options.debounceMs - Debounce delay in milliseconds (default: 300)
 * @param {number} options.minSearchLength - Minimum characters before search triggers (default: 2)
 * @param {boolean} options.loadOnMount - Whether to load initial data on mount (default: true)
 * @param {Function} options.transformResponse - Optional function to transform response data
 * @param {Object} options.extraParams - Additional query parameters to include in all requests (default: {})
 *
 * @returns {Object} Hook return value
 * @returns {Array} items - Current list of items
 * @returns {boolean} loading - Whether initial load is in progress
 * @returns {boolean} searchLoading - Whether search is in progress
 * @returns {Function} setSearchInput - Function to update search input (pass to onInputChange)
 * @returns {Object} autocompleteProps - Props to spread on MUI Autocomplete
 *
 * @example
 * const { items, autocompleteProps } = useServerSearch('/api/parts/');
 *
 * <Autocomplete
 *   options={items}
 *   {...autocompleteProps}
 *   // your custom props
 * />
 */
export function useServerSearch(endpoint, options = {}) {
  const {
    debounceMs = 300,
    minSearchLength = 2,
    loadOnMount = true,
    transformResponse = null,
    extraParams = {},
  } = options;

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(loadOnMount);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchInput, setSearchInputInternal] = useState('');
  const debounceRef = useRef(null);
  const mountedRef = useRef(true);
  // Latest request wins. Responses can arrive out of order: the unfiltered first page a dialog
  // loads on open is slower than the typed search, and landing last it replaced the search
  // results with unrelated rows under the user's query.
  const requestSeqRef = useRef(0);

  // Stable serialization of extraParams for dependency tracking
  const extraParamsKey = useMemo(() => JSON.stringify(extraParams), [extraParams]);

  // Build query string from extra params
  const buildExtraQuery = useCallback(() => {
    const params = JSON.parse(extraParamsKey);
    const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== null);
    if (entries.length === 0) return '';
    return entries.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`).join('&');
  }, [extraParamsKey]);

  // Parse response handling both paginated and non-paginated
  const parseResponse = useCallback((response) => {
    const data = response.data.results || response.data;
    const parsed = Array.isArray(data) ? data : [];
    return transformResponse ? transformResponse(parsed) : parsed;
  }, [transformResponse]);

  // Load initial items (first page)
  const loadInitial = useCallback(async () => {
    const seq = ++requestSeqRef.current;
    try {
      setLoading(true);
      const separator = endpoint.includes('?') ? '&' : '?';
      const extra = buildExtraQuery();
      const extraPart = extra ? `&${extra}` : '';
      const response = await axios.get(`${endpoint}${separator}page_size=50${extraPart}`);
      if (mountedRef.current && seq === requestSeqRef.current) {
        setItems(parseResponse(response));
      }
    } catch (err) {
      console.error(`Error loading from ${endpoint}:`, err);
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [endpoint, parseResponse, buildExtraQuery]);

  // Search with query (typeahead mode for autocomplete)
  const search = useCallback(async (query) => {
    if (!query || query.length < minSearchLength) {
      loadInitial();
      return;
    }

    const seq = ++requestSeqRef.current;
    try {
      setSearchLoading(true);
      const separator = endpoint.includes('?') ? '&' : '?';
      const extra = buildExtraQuery();
      const extraPart = extra ? `&${extra}` : '';
      const response = await axios.get(`${endpoint}${separator}search=${encodeURIComponent(query)}&typeahead=true${extraPart}`);
      if (mountedRef.current && seq === requestSeqRef.current) {
        setItems(parseResponse(response));
      }
    } catch (err) {
      console.error(`Error searching ${endpoint}:`, err);
    } finally {
      if (mountedRef.current) {
        setSearchLoading(false);
      }
    }
  }, [endpoint, minSearchLength, loadInitial, parseResponse, buildExtraQuery]);

  // Handle search input change with debounce
  const setSearchInput = useCallback((value) => {
    setSearchInputInternal(value);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      search(value);
    }, debounceMs);
  }, [search, debounceMs]);

  // Handle Autocomplete onInputChange - only respond to user typing
  const handleInputChange = useCallback((event, value, reason) => {
    if (reason === 'input') {
      setSearchInput(value);
    }
  }, [setSearchInput]);

  // Load initial data on mount
  useEffect(() => {
    mountedRef.current = true;
    if (loadOnMount) {
      loadInitial();
    }
    return () => {
      mountedRef.current = false;
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [loadOnMount, loadInitial]);

  // Reset function for when dialog reopens
  const reset = useCallback(() => {
    setSearchInputInternal('');
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    loadInitial();
  }, [loadInitial]);

  // Props to spread directly on MUI Autocomplete
  const autocompleteProps = {
    filterOptions: (x) => x, // Disable client-side filtering - server handles it
    onInputChange: handleInputChange,
    loading: searchLoading || loading,
  };

  return {
    items,
    loading,
    searchLoading,
    searchInput,
    setSearchInput,
    reset,
    loadInitial,
    autocompleteProps,
  };
}

export default useServerSearch;
